import { useEffect, useMemo } from "react";
import { arrayOf, bool, func, number, object, oneOf, shape } from "prop-types";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import omit from "lodash/omit";
import { yupResolver } from "@hookform/resolvers/yup";
import { TEAM_ROLES, PROJECT_VISIBILITY } from "@utils";
import { Button, Autocomplete } from "@components";
import { capitalize, CircularProgress, Divider, Grid } from "@material-ui/core";

const teamUserInfoFormSchema = (t, defaultValues) =>
  yup.object().shape({
    teamRole: yup.number().nullable(true).default(defaultValues?.teamRole),
    technicalStack: yup
      .array()
      .of(yup.number())
      .default(defaultValues?.technicalStack || []),
    professionalLevel: yup
      .number()
      .nullable(true)
      .default(defaultValues?.professionalLevel),
    projectsVisibility: yup
      .string()
      .oneOf([PROJECT_VISIBILITY.ALL, PROJECT_VISIBILITY.ASSIGNED])
      .default(defaultValues?.projectsVisibility),
  });

export const TeamUserInfoForm = ({
  onSubmit,
  onCancel,
  defaultValues,
  allowRoleChange,
  dataLoading,
  teamRoles,
  professionalLevels,
  stackTypes,
}) => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    reset: resetForm,
    watch,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(teamUserInfoFormSchema(t, defaultValues)),
    defaultValues: defaultValues,
  });

  const selectedTeamRole = watch("teamRole");
  const selectedProjectsVisibility = watch("projectsVisibility");
  const isManagerOrAdmin = [TEAM_ROLES.ADMIN, TEAM_ROLES.MANAGER].includes(selectedTeamRole);

  const projectsVisibilityHelperText = useMemo(() => {
    if(selectedTeamRole === TEAM_ROLES.ADMIN)
      return t("views.team.projects_visibility.for_admin");
    if(selectedTeamRole === TEAM_ROLES.MANAGER)
      return t("views.team.projects_visibility.for_manager");
    if(allowRoleChange)
      return;
    if(selectedProjectsVisibility === PROJECT_VISIBILITY.ALL)
      return t("views.team.projects_visibility.helper_all");
    if(selectedProjectsVisibility === PROJECT_VISIBILITY.ASSIGNED)
      return t("views.team.projects_visibility.helper_assigned");
  }, [ selectedTeamRole, selectedProjectsVisibility ])

  useEffect(() => {
    if(isManagerOrAdmin && selectedProjectsVisibility === PROJECT_VISIBILITY.ASSIGNED)
      setValue("projectsVisibility", PROJECT_VISIBILITY.ALL)
  }, [ isManagerOrAdmin, selectedProjectsVisibility ]);

  const submitHandler = async (data) => {
    const result = await onSubmit(
      [TEAM_ROLES.ADMIN, TEAM_ROLES.MANAGER].includes(selectedTeamRole)
        ? omit(data, "projectsVisibility")
        : data
    );

    if (result) {
      const { errors, reset } = result;

      if (reset) {
        resetForm(defaultValues);
        return;
      }

      errors?.forEach(({ field, message }) => {
        setError(field, { message });
      });
    }
  };

  return (
    <form
      autoComplete="on"
      aria-label="teamUserInfo form"
      onSubmit={handleSubmit(submitHandler)}
    >
      <Grid item container>
        <Controller
          name="teamRole"
          control={control}
          render={({ field: { onChange, value, name } }) => (
            <Autocomplete
              id="teamRoleSelect"
              value={value}
              options={teamRoles}
              name={name}
              idKey="id"
              labelKey="name"
              inputLabel={t("views.team.role")}
              fullWidth
              disabled={!allowRoleChange}
              onChange={onChange}
              size="small"
              dataLoading={dataLoading}
            />
          )}
        />
        <Controller
          name="professionalLevel"
          control={control}
          render={({ field: { onChange, value, name } }) => (
            <Autocomplete
              id="professionalLevelSelect"
              value={value}
              options={professionalLevels}
              name={name}
              fullWidth
              idKey="id"
              labelKey="name"
              inputLabel={t("views.team.level")}
              labelFormat={(l) => capitalize(l)}
              onChange={onChange}
              size="small"
              dataLoading={dataLoading}
            />
          )}
        />
        <Controller
          name="technicalStack"
          control={control}
          render={({ field: { onChange, value, name } }) => (
            <Autocomplete
              id="technicalStackSelect"
              value={value}
              options={stackTypes}
              name={name}
              fullWidth
              multiple
              className="h-max"
              idKey="id"
              labelKey="name"
              inputLabel={t("views.team.stack")}
              onChange={onChange}
              size="small"
              dataLoading={dataLoading}
            />
          )}
        />
        <Controller
          name="projectsVisibility"
          control={control}
          render={({ field: { onChange, value, name } }) => (
            <Autocomplete
              id="projectsVisibilitySelect"
              value={value}
              options={[
                { id: PROJECT_VISIBILITY.ALL, name: t("views.team.projects_visibility.all") },
                { id: PROJECT_VISIBILITY.ASSIGNED, name: t("views.team.projects_visibility.assigned") }
              ]}
              name={name}
              fullWidth
              idKey="id"
              labelKey="name"
              inputLabel={t("views.team.projects_visibility.title")}
              onChange={onChange}
              size="small"
              disableClearable
              disabled={!allowRoleChange}
              getOptionDisabled={(option) => (
                option.id === PROJECT_VISIBILITY.ASSIGNED && isManagerOrAdmin
              )}
              helperText={projectsVisibilityHelperText}
            />
          )}
        />
        
      </Grid>

      <Divider className="mt-2 -mx-6 mb-6" />

      <Grid item container justifyContent="flex-end">
        <Button
          variant="contained"
          color="secondary"
          onClick={onCancel}
          className="mr-2"
        >
          {t("common.cancel")}
        </Button>
        <Button
          isSubmit
          name="submit"
          variant="contained"
          endIcon={
            isSubmitting ? (
              <CircularProgress
                aria-label="progress indicator"
                size={20}
                color="inherit"
              />
            ) : undefined
          }
        >
          {t("common.save")}
        </Button>
      </Grid>
    </form>
  );
};

TeamUserInfoForm.propTypes = {
  onSubmit: func.isRequired,
  onCancel: func.isRequired,
  defaultValues: shape({
    teamRole: number,
    technicalStack: arrayOf(number),
    professionalLevel: number,
    projectsVisibility: oneOf([PROJECT_VISIBILITY.ALL, PROJECT_VISIBILITY.ASSIGNED]),
  }),
  allowRoleChange: bool,
  dataLoading: bool,
  teamRoles: arrayOf(object),
  professionalLevels: arrayOf(object),
  stackTypes: arrayOf(object),
};
